import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  Typography,
  Stack,
  Chip,
  IconButton,
  CircularProgress,
} from '@mui/material';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import CloseIcon from '@mui/icons-material/Close';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import HistoryToggleOffIcon from '@mui/icons-material/HistoryToggleOff';
import dayjs from 'dayjs';
import buddhistEra from 'dayjs/plugin/buddhistEra';
import 'dayjs/locale/th';
import api from '../../../../utils/functions/api';
import {
  STATUS_MAP,
  WORK_OPTIONS,
  WORK_METHOD_OPTIONS,
  FINANCE_OPTIONS,
  BG_USAGE_OPTIONS,
  REPORT_OPTIONS,
  DOC_OPTIONS,
  PO_STATE_OPTIONS,
} from '../../../../components/Table/projectDashboard/constants';

dayjs.extend(buddhistEra);

// ── ป้ายกำกับฟิลด์ที่แก้ไขได้จาก patchDashboard (project.service.js) — ใช้แสดงในไดอะแกรม diff ──
// ตั้งใจใช้ข้อความเดียวกับที่แสดงจริงบนหน้าแดชบอร์ดโปรเจกต์เดี่ยว (ProjectSingleDashboard.js, sheet 0
// ของ ProjectPopup.js) ทั้งสองภาษา ให้ตรงกับสวิตช์ 🇹🇭/🇬🇧 บนหน้านั้น (ดู popupLang ใน ProjectPopup.js) —
// fallback เป็นคำอธิบายกว้างๆ เฉพาะฟิลด์ที่ไม่มีช่องแก้ไขตรงๆ ในหน้านี้ (เช่น dash_* ที่ derive มาจาก
// cascade สถานะ ไม่มีข้อความ EN ต้นทางให้อ้างอิงตรงๆ)
const FIELD_LABELS = {
  progress: { th: 'ความคืบหน้า', en: 'Progress' },
  dashboard_note: { th: 'หมายเหตุ', en: 'Notes' },
  operation_status: { th: 'สถานะโครงการ', en: 'Project Status' },
  to_spend: { th: 'ต้องใช้อีก', en: 'To Spend' },
  bg_usage_category: { th: 'หมวดการใช้ BUDGET', en: 'Budget mode' },
  dash_commitment: { th: 'ภาระผูกพัน', en: 'Commitment' },
  dash_work: { th: 'ความคืบหน้างาน', en: 'Work Progress' },
  dash_delivery: { th: 'ส่งมอบงาน', en: 'Delivery' },
  dash_disbursement: { th: 'การเบิกจ่าย', en: 'Disbursement' },
  dash_warranty: { th: 'สถานะประกัน', en: 'Warranty Status' },
  dash_payment: { th: 'การรับชำระเงิน', en: 'Payment' },
  dash_billing: { th: 'การวางบิล', en: 'Billing' },
  seed_received: { th: 'รับเงินมาแล้ว', en: 'Received' },
  seed_billed_waiting: { th: 'Invoiced [ VAT incl ]', en: 'Invoiced [ VAT incl ]' },
  seed_ap_pending: { th: 'เงินทดรองค้างจ่าย', en: 'AP Pending' },
  work_status: { th: 'ด้านงาน', en: 'Work' },
  work_method: { th: 'วิธีดำเนินการ', en: 'Work Method' },
  finance_status: { th: 'ด้านการเงิน', en: 'Finance' },
  legal_debt: { th: 'เจ้าหนี้ค้างจ่าย', en: 'Overdue Payable' },
  legal_lawsuit: { th: 'ฟ้องร้อง', en: 'Lawsuit' },
  legal_judged: { th: 'พิพากษาแล้ว', en: 'Judged' },
  legal_warranty: { th: 'Warrant/Guarantee', en: 'Warrant/Guarantee' },
  warranty_end_date: { th: 'วันครบกำหนดรับประกัน', en: 'Warranty end date' },
  delivery_date: { th: 'ส่งงาน วันที่', en: 'Delivery Date' },
  report_status: { th: 'รายงาน', en: 'Report' },
  doc_status: { th: 'เอกสาร', en: 'Documents' },
  po_state: { th: 'สถานะ PO', en: 'PO status' },
  payment_cycle_day: { th: 'รอบจ่ายเงิน', en: 'Pay Cycle' },
};

// รหัสค่าที่ใช้ซ้ำในหลายฟิลด์ dash_* ที่ไม่มี option map กลางของตัวเอง
const GENERIC_CODE_LABELS = {
  has: { th: 'มี', en: 'Has' },
  no: { th: 'ไม่มี', en: 'No' },
  none: { th: 'ไม่มี', en: 'None' },
  na: { th: 'ไม่เกี่ยวข้อง', en: 'N/A' },
  done: { th: 'เสร็จแล้ว', en: 'Done' },
  partial: { th: 'บางส่วน', en: 'Partial' },
  warranty: { th: 'อยู่ในประกัน', en: 'Under warranty' },
  expired: { th: 'หมดประกัน', en: 'Expired' },
  no_data: { th: 'ไม่มีข้อมูล', en: 'No data' },
  waiting: { th: 'รอดำเนินการ', en: 'Waiting' },
  received: { th: 'รับแล้ว', en: 'Received' },
};

const ACTION_META = {
  CREATE: { label: { th: 'สร้างโครงการ', en: 'Created' }, color: '#059669', icon: AddCircleIcon },
  UPDATE: { label: { th: 'แก้ไขข้อมูล', en: 'Updated' }, color: '#2563eb', icon: EditIcon },
  DELETE: { label: { th: 'ลบโครงการ', en: 'Deleted' }, color: '#dc2626', icon: DeleteIcon },
};

function pick(entry, uiLang) {
  if (!entry) return null;
  return entry[uiLang] ?? entry.th ?? entry.en ?? null;
}

function optionLabel(list, key) {
  return list.find((o) => o.key === key)?.label;
}

function formatFieldValue(field, value, uiLang) {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? pick({ th: 'ใช่', en: 'Yes' }, uiLang) : pick({ th: 'ไม่ใช่', en: 'No' }, uiLang);
  // ตัวเลือกสถานะ (work_status/finance_status/ฯลฯ) มีป้าย EN แยกเฉพาะจุดที่ใช้จริงใน
  // ProjectSingleDashboard.js เท่านั้น (ไม่ได้อยู่ใน constants.js กลาง) — ยังไม่พอร์ตมาที่นี่
  // เลยโชว์เป็นไทยเสมอสำหรับ "ค่า" ของตัวเลือกเหล่านี้ ต่างจาก "ชื่อ field" ที่สลับภาษาได้แล้ว
  if (field === 'operation_status') return STATUS_MAP[value]?.label || value;
  if (field === 'work_status') return optionLabel(WORK_OPTIONS, value) || value;
  if (field === 'work_method') return optionLabel(WORK_METHOD_OPTIONS, value) || value;
  if (field === 'finance_status') return optionLabel(FINANCE_OPTIONS, value) || value;
  if (field === 'bg_usage_category') return optionLabel(BG_USAGE_OPTIONS, value) || value;
  if (field === 'report_status') return optionLabel(REPORT_OPTIONS, value) || value;
  // 'po_received' เคยเป็นตัวเลือกของการ์ดเอกสาร (ย้ายไปการ์ด "สถานะ PO" แล้ว) — ประวัติเก่ายังต้องอ่านออก
  if (field === 'doc_status') return optionLabel(DOC_OPTIONS, value) || optionLabel(PO_STATE_OPTIONS, value) || value;
  if (field === 'po_state') return optionLabel(PO_STATE_OPTIONS, value) || value;
  if (field === 'warranty_end_date' || field === 'delivery_date') {
    const d = dayjs(value);
    if (!d.isValid()) return String(value);
    return uiLang === 'en' ? d.locale('en').format('D MMM YYYY') : d.locale('th').format('D MMM BBBB');
  }
  if (
    ['to_spend', 'seed_received', 'seed_billed_waiting', 'seed_ap_pending'].includes(field)
    && typeof value === 'number'
  ) {
    return value.toLocaleString(uiLang === 'en' ? 'en-US' : 'th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  if (field === 'progress' && typeof value === 'number') return `${value}%`;
  if (field === 'payment_cycle_day') return pick({ th: `วันที่ ${value}`, en: `Day ${value}` }, uiLang);
  if (GENERIC_CODE_LABELS[value]) return pick(GENERIC_CODE_LABELS[value], uiLang);
  return String(value);
}

// วันที่ตามเวลาไทยของ timestamp UTC — ใช้กลุ่มรายการแก้ไขเป็น "เวอร์ชัน" ต่อวัน (pattern เดียวกับ
// ที่ backend ใช้คำนวณ "วันนี้ตามเวลาไทย" ทั่วทั้งระบบ)
function thaiDateStr(isoDate) {
  return new Date(isoDate).toLocaleDateString('en-CA', { timeZone: 'Asia/Bangkok' });
}

function formatVersionDate(dateStr, uiLang) {
  const d = dayjs(dateStr);
  return uiLang === 'en' ? d.locale('en').format('D MMM YYYY') : d.locale('th').format('D MMM BBBB');
}

// แผนที่ประวัติการแก้ไขของโครงการเดียว — วงกลมแต่ละจุด = เวอร์ชัน (รวมการแก้ไขทั้งหมดของวันนั้น)
// คลิกจุดเพื่อดูรายละเอียดว่าใครแก้ฟิลด์ไหนจากอะไรเป็นอะไร แสดงเป็นไดอะแกรม chip เก่า → ใหม่ —
// สลับภาษาตาม uiLang (ส่งต่อมาจาก popupLang ของ ProjectPopup.js เดียวกับที่คุม ProjectSingleDashboard.js)
export function ProjectHistoryDialog({ open, onClose, projectId, projectName, uiLang = 'en' }) {
  const U = (th, en) => (uiLang === 'en' ? en : th);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    if (!open || !projectId) return undefined;
    let cancelled = false;
    setLoading(true);
    api
      .get(`${process.env.REACT_APP_API_URL}/activity-log`, {
        params: { module: 'PROJECT', resourceId: projectId, size: 300 },
      })
      .then(({ data }) => {
        if (cancelled) return;
        setRows(data?.rows || []);
        setSelectedDay(null);
      })
      .catch((e) => console.error('[ProjectHistoryDialog] load failed', e))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, projectId]);

  const versions = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      const dateStr = thaiDateStr(r.createdAt);
      if (!map.has(dateStr)) map.set(dateStr, []);
      map.get(dateStr).push(r);
    });
    return Array.from(map.entries())
      .map(([dateStr, entries]) => ({
        dateStr,
        entries: [...entries].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
      }))
      .sort((a, b) => (a.dateStr < b.dateStr ? 1 : -1));
  }, [rows]);

  useEffect(() => {
    if (versions.length && !selectedDay) setSelectedDay(versions[0].dateStr);
  }, [versions, selectedDay]);

  const activeVersion = versions.find((v) => v.dateStr === selectedDay);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { maxHeight: '88vh', borderRadius: 3, overflow: 'hidden' } }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* ── หัว dialog: ไอคอนวงกลม + ชื่อโครงการ บนพื้นไล่สีอ่อนๆ ── */}
        <Box
          sx={{
            px: 2.5,
            py: 1.75,
            background: 'linear-gradient(135deg, rgb(var(--color-primary-50)) 0%, #f5f3ff 100%)',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                bgcolor: '#6366f1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                boxShadow: '0 2px 6px rgba(99,102,241,0.35)',
              }}
            >
              <HistoryIcon sx={{ color: '#fff', fontSize: 20 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={800}>
                {U('ประวัติการแก้ไข', 'Edit History')}
              </Typography>
              {projectName && (
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                  {projectName}
                </Typography>
              )}
            </Box>
            {versions.length > 0 && (
              <Chip
                size="small"
                label={U(`${versions.length} เวอร์ชัน`, `${versions.length} version${versions.length > 1 ? 's' : ''}`)}
                sx={{ bgcolor: '#ffffffb0', color: 'rgb(var(--color-primary-600))', fontWeight: 700 }}
              />
            )}
            <IconButton size="small" onClick={onClose} sx={{ bgcolor: '#ffffff70', '&:hover': { bgcolor: '#ffffffb0' } }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 8 }}>
            <CircularProgress size={30} thickness={4} />
            <Typography variant="caption" color="text.disabled">
              {U('กำลังโหลดประวัติ...', 'Loading history...')}
            </Typography>
          </Box>
        ) : versions.length === 0 ? (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <HistoryToggleOffIcon sx={{ fontSize: 44, color: '#e2e8f0', mb: 1 }} />
            <Typography color="text.disabled">{U('ยังไม่มีประวัติการแก้ไข', 'No edit history yet')}</Typography>
          </Box>
        ) : (
          <Stack direction={{ xs: 'column', md: 'row' }} sx={{ minHeight: 380 }}>
            {/* ── แผนที่เวอร์ชัน (ซ้าย): 1 จุด = 1 วันที่มีการแก้ไข ── */}
            <Box
              sx={{
                width: { xs: '100%', md: 270 },
                flexShrink: 0,
                borderRight: { md: '1px solid' },
                borderBottom: { xs: '1px solid', md: 'none' },
                borderColor: 'divider',
                maxHeight: { md: '68vh' },
                overflowY: 'auto',
                bgcolor: '#fbfbfe',
                py: 1.5,
                px: 1,
              }}
            >
              <Typography
                variant="overline"
                sx={{ display: 'block', px: 1, mb: 0.5, color: 'text.disabled', fontWeight: 700, letterSpacing: '0.06em' }}
              >
                {U('เส้นเวลา', 'Timeline')}
              </Typography>
              <Timeline sx={{ m: 0, p: 0, '& .MuiTimelineItem-root:before': { flex: 0, padding: 0 } }}>
                {versions.map((v, i) => {
                  const active = v.dateStr === selectedDay;
                  const meta = ACTION_META[v.entries[0]?.action] || ACTION_META.UPDATE;
                  const DotIcon = meta.icon;
                  return (
                    <TimelineItem key={v.dateStr} onClick={() => setSelectedDay(v.dateStr)} sx={{ cursor: 'pointer', minHeight: 64 }}>
                      <TimelineSeparator>
                        <TimelineDot
                          sx={{
                            width: 30,
                            height: 30,
                            m: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: active ? meta.color : '#fff',
                            border: '2px solid',
                            borderColor: active ? meta.color : '#cbd5e1',
                            boxShadow: active ? `0 0 0 4px ${meta.color}22` : 'none',
                            transition: 'all .15s',
                          }}
                        >
                          <DotIcon sx={{ fontSize: 15, color: active ? '#fff' : '#94a3b8' }} />
                        </TimelineDot>
                        {i < versions.length - 1 && <TimelineConnector sx={{ bgcolor: '#e2e8f0', width: 2 }} />}
                      </TimelineSeparator>
                      <TimelineContent sx={{ py: 0.2, pb: 2 }}>
                        <Box
                          sx={{
                            borderRadius: 1.5,
                            px: 1,
                            py: 0.5,
                            bgcolor: active ? `${meta.color}12` : 'transparent',
                            transition: 'background-color .15s',
                            '&:hover': { bgcolor: active ? `${meta.color}12` : '#f1f5f9' },
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight={active ? 700 : 500}
                            sx={{ color: active ? meta.color : 'text.primary' }}
                          >
                            {formatVersionDate(v.dateStr, uiLang)}
                          </Typography>
                          <Typography variant="caption" color="text.disabled">
                            {U(`${v.entries.length} การแก้ไข`, `${v.entries.length} edit${v.entries.length > 1 ? 's' : ''}`)}
                          </Typography>
                        </Box>
                      </TimelineContent>
                    </TimelineItem>
                  );
                })}
              </Timeline>
            </Box>

            {/* ── รายละเอียด (ขวา): ทุกการแก้ไขของวันที่เลือก แสดงเป็นไดอะแกรม เก่า → ใหม่ ── */}
            <Box sx={{ flex: 1, minWidth: 0, p: 2, overflowY: 'auto', maxHeight: { md: '68vh' }, bgcolor: '#fff' }}>
              {activeVersion && (
                <>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                    {formatVersionDate(activeVersion.dateStr, uiLang)}
                  </Typography>
                  <Stack spacing={1.5}>
                    {activeVersion.entries.map((entry) => {
                      const meta = ACTION_META[entry.action] || ACTION_META.UPDATE;
                      const Icon = meta.icon;
                      const fieldKeys = entry.before && entry.after ? Object.keys(entry.after) : [];
                      return (
                        <Box
                          key={entry._id}
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderLeft: '4px solid',
                            borderLeftColor: meta.color,
                            borderRadius: 2,
                            p: 1.5,
                            bgcolor: '#fbfbfe',
                            boxShadow: '0 1px 3px rgba(15,23,42,0.05)',
                          }}
                        >
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: fieldKeys.length ? 1.25 : 0 }}>
                            <Box
                              sx={{
                                width: 22,
                                height: 22,
                                borderRadius: '50%',
                                bgcolor: `${meta.color}1a`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                              }}
                            >
                              <Icon sx={{ fontSize: 14, color: meta.color }} />
                            </Box>
                            <Chip
                              label={pick(meta.label, uiLang)}
                              size="small"
                              sx={{ bgcolor: `${meta.color}1a`, color: meta.color, fontWeight: 700, height: 22 }}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', whiteSpace: 'nowrap' }}>
                              {dayjs(entry.createdAt).format('HH:mm')} · {entry.actorName || entry.actorUsername || U('ไม่ระบุ', 'Unknown')}
                            </Typography>
                          </Stack>
                          {fieldKeys.length > 0 ? (
                            <Stack spacing={0.6}>
                              {fieldKeys.map((key) => (
                                <Stack
                                  key={key}
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                  flexWrap="wrap"
                                  sx={{ rowGap: 0.5 }}
                                >
                                  <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: meta.color, flexShrink: 0 }} />
                                  <Typography
                                    variant="caption"
                                    sx={{ width: 128, flexShrink: 0, fontWeight: 600, color: 'text.secondary' }}
                                  >
                                    {pick(FIELD_LABELS[key], uiLang) || key}
                                  </Typography>
                                  <Chip
                                    size="small"
                                    label={formatFieldValue(key, entry.before[key], uiLang)}
                                    sx={{
                                      bgcolor: '#fee2e2',
                                      color: '#991b1b',
                                      textDecoration: 'line-through',
                                      height: 22,
                                      fontSize: '0.72rem',
                                    }}
                                  />
                                  <ArrowRightAltIcon sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }} />
                                  <Chip
                                    size="small"
                                    label={formatFieldValue(key, entry.after[key], uiLang)}
                                    sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 700, height: 22, fontSize: '0.72rem' }}
                                  />
                                </Stack>
                              ))}
                            </Stack>
                          ) : (
                            <Typography variant="body2" sx={{ ml: 3.75 }}>{entry.summary}</Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Stack>
                </>
              )}
            </Box>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}
